import { authApiCall } from "@/services/http.hook";
import makeUrl from "@/services/makeUrl";
import store from "@/store/store";
import { toNumber, roundMoneyLegacyEpsilon } from "@/pricing";

// ─── Shared utilities ────────────────────────────────────────────────────────

/**
 * Round a money value according to the store's tax_round setting:
 *   nearest (default) — round to 2 dp (Math.round + EPSILON, ties to +Inf)
 *   no                — no rounding, preserve full precision
 *   order_level_distributed — same as nearest for per-value rounding
 *
 * Delegates to the single pricing engine's exact-legacy rounding helper.
 */
function roundMoney(value) {
  let taxRound;
  try {
    taxRound = store.getters["config/store"]?.tax_round;
  } catch (_) { /* store not ready yet — fall through to default */ }
  return roundMoneyLegacyEpsilon(value, taxRound);
}

function normalizeId(value) {
  if (value === null || value === undefined) return null;
  return String(value);
}

function normalizeDays(dayOfWeek) {
  if (!dayOfWeek) return [];
  if (Array.isArray(dayOfWeek)) return dayOfWeek.map((d) => String(d));
  return String(dayOfWeek).split(",").map((d) => d.trim()).filter(Boolean);
}

// item.id must be the product ID (callers must enrich before passing)
function getItemProductId(item) {
  return normalizeId(item.id || item.product);
}

function getItemCategoryIds(item) {
  if (Array.isArray(item.categories)) {
    return item.categories.map(normalizeId);
  }
  return [];
}

// Hard, promotion-independent gate: an item with allow_discounts === false is
// never eligible for any promotion, automatic or manual, regardless of scope.
function isItemDiscountable(item) {
  return item.allow_discounts !== false;
}

function getLineBaseTotal(item) {
  return roundMoney(toNumber(item.price) * toNumber(item.quantity || 1));
}

// ─── Scheduling / eligibility ─────────────────────────────────────────────────

// apply_to targets the order's discount_employee (set via the Employees tab in
// the customer modal, order.discount_employee) — NOT emp_open/emp_close, which
// only record who processed the sale, and NOT the order's regular customer.
// customer_groups isn't wired up (no customer-group context reaches this
// evaluator), so it's treated as never-applicable, same as before.
function isPromotionApplicableToEmployee(promotion, employee) {
  const applyTo = promotion.apply_to || "all_customers";
  if (applyTo === "all_customers") return true;
  if (!employee || employee.id === undefined || employee.id === null) return false;

  if (applyTo === "all_employees") return true;

  if (applyTo === "specific_employees") {
    const employeeIds = (promotion.employees || []).map(String);
    return employeeIds.includes(String(employee.id));
  }

  if (applyTo === "employee_roles") {
    // employee.role on the synced employee list is new_role.id (EmployeeSerializer).
    const roleIds = (promotion.employee_roles || []).map(String);
    if (employee.role === undefined || employee.role === null) return false;
    return roleIds.includes(String(employee.role));
  }

  return false; // customer_groups, or anything unrecognized
}

// Schedule/channel only — no audience context (customer/employee) involved.
// This is the check used to build the general "what's currently live" list
// (promotions/activePromotions getter), which has no cart/order in scope yet.
// Do NOT fold the employee/apply_to check in here — a promotion restricted to
// all_employees/specific_employees/employee_roles is still "in its scheduled
// window" even though nobody has picked an employee on this order yet; it's
// applyPromotionsToCart (which has that context) that decides applicability.
function isPromotionInScheduleWindow(promotion, channelFilter = "point_of_sale") {
  if (!promotion?.is_active) return false;

  // Evaluate only promotions for the requested channel (plus "all").
  // POS-created orders use point_of_sale, online orders use online_order.
  const channel = promotion.sales_channel || "all";
  if (channel !== "all" && channel !== channelFilter) return false;

  const now = new Date();
  const currentDate = now.toISOString().split("T")[0];
  if (promotion.start_date && currentDate < promotion.start_date) return false;
  if (promotion.end_date && currentDate > promotion.end_date) return false;

  const days = normalizeDays(promotion.day_of_week);
  if (days.length > 0) {
    const currentDay = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    if (!days.includes(currentDay)) return false;
  }

  if (promotion.start_time && promotion.end_time) {
    const h = String(now.getHours()).padStart(2, "0");
    const m = String(now.getMinutes()).padStart(2, "0");
    const s = String(now.getSeconds()).padStart(2, "0");
    const currentTime = `${h}:${m}:${s}`;
    const { start_time, end_time } = promotion;
    if (start_time <= end_time) {
      return currentTime >= start_time && currentTime <= end_time;
    }
    // Overnight wrap-around
    return currentTime >= start_time || currentTime <= end_time;
  }

  return true;
}

// Full check: schedule window + audience (apply_to). This is the one used
// inside applyPromotionsToCart, where cart/order context (the employee) exists.
function isPromotionScheduledNow(promotion, channelFilter = "point_of_sale", context = {}) {
  if (!isPromotionInScheduleWindow(promotion, channelFilter)) return false;
  return isPromotionApplicableToEmployee(promotion, context.employee);
}

// ─── Rule-config evaluators ───────────────────────────────────────────────────

function emptyDiscountResult(cartItems) {
  return {
    items: cartItems.map((item) => ({
      ...item,
      discount: 0,
      wholeDiscount: 0,
      promotionLabels: []
    })),
    appliedPromotions: [],
    discountTotal: 0
  };
}

function chooseBetterResult(current, candidate) {
  if (!candidate) return current;
  if (!current || candidate.totalDiscount > current.totalDiscount) return candidate;
  return current;
}

function isStackablePromotion(promotion) {
  return !!(promotion.rule_config && promotion.rule_config.stackable === true);
}

/**
 * Computes a single AOP (product/line-level) promotion's result against
 * whatever's already been reserved on each line, or null if inapplicable.
 */
function computeAOPResult(promotion, items, reservedLineDiscounts) {
  const config = promotion.rule_config;
  if (!config) return null;
  const reward = config.reward || {};
  if (reward.target !== "products") return null;
  const condition = config.condition || {};
  if (condition.type === "buys_products") return null; // BXY path, not AOP

  const value = toNumber(reward.value);
  if (value <= 0) return null;
  const isFlatAmount = (reward.discount_mode || "percentage") === "amount";
  const include = reward.include || {};

  const lineDiscounts = {};
  let totalDiscount = 0;

  items.forEach((item, index) => {
    if (!isItemDiscountable(item)) return;

    let matches = false;
    const scope = include.scope || "all_products";
    if (scope === "all_products") {
      matches = true;
    } else if (scope === "products") {
      const productIds = (include.products || []).map(String);
      matches = productIds.length > 0 && productIds.includes(String(getItemProductId(item)));
    } else if (scope === "categories") {
      const categoryIds = (include.categories || []).map(String);
      const itemCategoryIds = getItemCategoryIds(item).map(String);
      matches = categoryIds.length > 0 && categoryIds.some((cid) => itemCategoryIds.includes(cid));
    }
    if (!matches) return;

    const lineBase = getLineBaseTotal(item);
    const remainingBase = Math.max(lineBase - toNumber(reservedLineDiscounts[index]), 0);
    if (remainingBase <= 0) return;

    const unitPrice = toNumber(item.price || 0);
    const itemQuantity = Math.floor(toNumber(item.quantity || 0));
    const discountBase = reward.discount_base || "each_product_price";
    let discountAmount;
    if (isFlatAmount) {
      if (discountBase === "each_product_price" && itemQuantity > 0) {
        discountAmount = Math.min(value, unitPrice) * itemQuantity;
      } else {
        discountAmount = Math.min(value, remainingBase);
      }
    } else {
      discountAmount = roundMoney(remainingBase * (value / 100));
    }
    discountAmount = Math.min(discountAmount, remainingBase);
    lineDiscounts[index] = Math.max(lineDiscounts[index] || 0, discountAmount);
  });

  Object.values(lineDiscounts).forEach((amount) => { totalDiscount += amount; });

  return { promotion, lineDiscounts, totalDiscount: roundMoney(totalDiscount) };
}

function buildUnitCandidates(items, matcher) {
  const units = [];
  items.forEach((item, itemIndex) => {
    if (!matcher(item)) return;
    const quantity = Math.floor(toNumber(item.quantity || 0));
    for (let i = 0; i < quantity; i++) {
      units.push({ itemIndex, unitPrice: toNumber(item.price) });
    }
  });
  return units;
}

/**
 * Computes a single BXY (Buy X Get Y) promotion's result against the cart as
 * it currently stands, or null if inapplicable. Unlike AOP/AOO, BXY discounts
 * specific discrete units rather than a percentage of remaining value, so
 * there's no "reserved amount" to compound against here — conflicts between
 * multiple BXY results (same unit awarded free twice) are resolved separately
 * by the caller via each result's claimed line indexes.
 */
function buildBXYCandidate(promotion, items) {
  const config = promotion.rule_config;
  if (!config) return null;
  const reward = config.reward || {};
  const condition = config.condition || {};

  const isBundle = reward.target === "bundle";
  const isBuysProducts = condition.type === "buys_products" && reward.target === "products";
  if (!isBundle && !isBuysProducts) return null;

  const bundlePrice = reward.target === "bundle" ? toNumber(reward.bundle_price) : 0;
  const value = bundlePrice || toNumber(reward.value);
  const getQuantity = Math.floor(toNumber(reward.quantity || 1));
  const buyQuantity = Math.floor(toNumber(condition.threshold || 1));
  if (buyQuantity <= 0 || getQuantity <= 0 || value <= 0) return null;

  const conditionInclude = condition.include || {};
  const rewardInclude = reward.include || {};

  const buildMatcher = (scopeConfig) => (item) => {
    if (!isItemDiscountable(item)) return false;
    const scope = scopeConfig?.scope || "all_products";
    if (scope === "all_products") return true;
    if (scope === "products") {
      const ids = (scopeConfig.products || []).map(String);
      return ids.length > 0 && ids.includes(String(getItemProductId(item)));
    }
    if (scope === "categories") {
      const ids = (scopeConfig.categories || []).map(String);
      const itemCats = getItemCategoryIds(item).map(String);
      return ids.length > 0 && ids.some((cid) => itemCats.includes(cid));
    }
    return false;
  };

  const buyMatcher = buildMatcher(conditionInclude);
  const getMatcher = rewardInclude.scope === "same_as_condition"
    ? buyMatcher
    : buildMatcher(rewardInclude);

  const buyUnits = buildUnitCandidates(items, buyMatcher);
  const getUnits = buildUnitCandidates(items, getMatcher);

  const lineDiscounts = {};
  let totalDiscount = 0;

  if (isBundle) {
    const numBundles = Math.floor(buyUnits.length / buyQuantity);
    if (numBundles <= 0) return null;

    const sortedBuyUnits = [...buyUnits].sort((a, b) => b.unitPrice - a.unitPrice);
    let bundleFullPrice = 0;
    for (let i = 0; i < numBundles * buyQuantity; i++) bundleFullPrice += sortedBuyUnits[i].unitPrice;
    const bundleDiscount = Math.max(bundleFullPrice - value * numBundles, 0);
    if (bundleDiscount <= 0) return null;

    const selectedUnits = sortedBuyUnits.slice(0, numBundles * buyQuantity);
    const selectedTotal = selectedUnits.reduce((s, u) => s + u.unitPrice, 0);
    let distributed = 0;
    selectedUnits.forEach((unit, idx) => {
      const isLast = idx === selectedUnits.length - 1;
      const share = isLast
        ? roundMoney(bundleDiscount - distributed)
        : roundMoney(bundleDiscount * (unit.unitPrice / selectedTotal));
      lineDiscounts[unit.itemIndex] = roundMoney((lineDiscounts[unit.itemIndex] || 0) + share);
      distributed = roundMoney(distributed + share);
    });
    totalDiscount = distributed;
  } else {
    const discountMode = reward.discount_mode || reward.discount_type;
    const conditionItems = items.filter(buyMatcher);
    const metric = condition.metric || "quantity";
    const qualifying = metric === "quantity"
      ? conditionItems.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0)
      : conditionItems.reduce((s, i) => s + (parseFloat(i.original_total_price) || 0), 0);
    if (qualifying < toNumber(condition.threshold)) return null;

    if (reward.quantity_mode === "up_to") {
      // "Buy X, get up to Y free" is cycle-based: each cycle consumes
      // (buyQuantity + getQuantity) qualifying units and awards getQuantity
      // of them — e.g. "buy 1 get 1 free" needs 2 total units in the cart
      // before 1 goes free, not just 1.
      const sameProductPool = rewardInclude.scope === "same_as_condition";
      const cycleSize = buyQuantity + getQuantity;
      let selectedUnits;

      if (sameProductPool) {
        // Walk the qualifying units in the order they were added to the
        // cart (append-only) and, for every complete group of cycleSize
        // units, discount the trailing getQuantity units — i.e. "buy N,
        // the next M are free," repeating as more units are added. This
        // keeps each completed pair's assignment stable as later items
        // are added, instead of the discount jumping to whichever units
        // are newest overall.
        const chronological = [...getUnits].sort((a, b) => a.itemIndex - b.itemIndex);
        selectedUnits = [];
        for (let i = 0; i + cycleSize <= chronological.length; i += cycleSize) {
          selectedUnits.push(...chronological.slice(i, i + cycleSize).slice(buyQuantity));
        }
      } else {
        // Reward targets a different scope than the condition (buy A, get
        // B): a cycle is earned per buyQuantity buy-units, capped by how
        // many get-eligible units are in the cart. Award the
        // most-recently-added get-eligible lines first.
        const cycles = Math.floor(buyUnits.length / buyQuantity);
        const awardedUnits = Math.min(cycles * getQuantity, getUnits.length);
        selectedUnits = [...getUnits].sort((a, b) => b.itemIndex - a.itemIndex).slice(0, awardedUnits);
      }

      if (selectedUnits.length === 0) return null;
      selectedUnits.forEach((unit) => {
        const unitDiscount = discountMode === "amount"
          ? roundMoney(Math.min(value, unit.unitPrice))
          : roundMoney(unit.unitPrice * (value / 100));
        if (unitDiscount > 0) {
          lineDiscounts[unit.itemIndex] = roundMoney((lineDiscounts[unit.itemIndex] || 0) + unitDiscount);
        }
      });
      totalDiscount = Object.values(lineDiscounts).reduce((s, v) => s + v, 0);
      if (totalDiscount <= 0) return null;
    } else {
      // "all_matching": discount every matching line in full, uncapped
      items.forEach((item, itemIndex) => {
        const isRewardItem = rewardInclude.scope === "same_as_condition" ? buyMatcher(item) : getMatcher(item);
        if (!isRewardItem) return;
        const itemBase = getLineBaseTotal(item);
        let itemDiscount = discountMode === "percentage"
          ? roundMoney(itemBase * (value / 100))
          : roundMoney(Math.min(value, itemBase));
        if (itemDiscount > 0) {
          totalDiscount += itemDiscount;
          lineDiscounts[itemIndex] = roundMoney((lineDiscounts[itemIndex] || 0) + itemDiscount);
        }
      });
      if (totalDiscount <= 0) return null;
    }
  }

  return { promotion, lineDiscounts, totalDiscount: roundMoney(totalDiscount) };
}

/**
 * Computes a single AOO (order-level) promotion's result against the
 * aggregate subtotal remaining after existingLineDiscounts (AOP/BXY) and
 * reservedTotal (AOO discount already committed by earlier results), or null
 * if inapplicable.
 */
function computeAOOResult(promotion, items, existingLineDiscounts, reservedTotal) {
  const config = promotion.rule_config;
  if (!config) return null;
  const reward = config.reward || {};
  if (reward.target !== "order") return null;

  const value = toNumber(reward.value);
  if (value <= 0) return null;

  const condition = config.condition || {};
  const minOrderAmount = condition.type === "order_subtotal" ? toNumber(condition.threshold) : 0;
  const isFlatAmount = (reward.discount_type || "percentage") === "amount";

  const eligibleSubtotal = items.reduce((sum, item) => sum + getLineBaseTotal(item), 0);
  if (eligibleSubtotal < minOrderAmount) return null;

  const baseDiscountableSubtotal = items.reduce((sum, item, index) => {
    if (!isItemDiscountable(item)) return sum;
    const remaining = Math.max(getLineBaseTotal(item) - toNumber(existingLineDiscounts[index]), 0);
    return sum + remaining;
  }, 0);
  // Lines shrink proportionally as each AOO result is distributed, so the
  // aggregate remaining subtotal is exactly the base minus what's already
  // been reserved by earlier (exclusive winner + prior stackable) results.
  const discountableSubtotal = Math.max(baseDiscountableSubtotal - reservedTotal, 0);

  const totalDiscount = isFlatAmount
    ? Math.min(value, discountableSubtotal)
    : roundMoney(discountableSubtotal * (value / 100));

  return { promotion, totalDiscount };
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Apply all active promotions to a cart.
 *
 * cartItems must be enriched before calling:
 *   { ...orderItem, id: orderItem.product, categories: product.categories }
 *
 * options.employee — the order's discount_employee (resolved via employeeById),
 * used to evaluate promotions targeted at employees/roles via apply_to.
 *
 * Returns { items, appliedPromotions, discountTotal } where item.discount and
 * item.wholeDiscount are TOTAL-LINE values (divide by quantity for per-unit).
 *
 * Stacking rule (applies uniformly across AOP/BXY/AOO — not per type): every
 * promotion competes for ONE shared "exclusive" slot by default (the single
 * highest-discount promotion overall wins, whichever type it is). A promotion
 * with rule_config.stackable = true skips that competition entirely and
 * always applies in addition to the exclusive winner, compounding on top of
 * whatever's left after it (and after any other stackable promotions applied
 * before it).
 */
export function applyPromotionsToCart(cartItems, promotions, options = {}) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return emptyDiscountResult([]);
  }

  const channel = options.channel || "point_of_sale";
  const eligiblePromotions = (promotions || []).filter((promotion) =>
    isPromotionScheduledNow(promotion, channel, { employee: options.employee })
  );
  if (eligiblePromotions.length === 0) {
    return emptyDiscountResult(cartItems);
  }

  const items = cartItems.map((item) => ({
    ...item,
    discount: 0,
    wholeDiscount: 0,
    promotionLabels: []
  }));

  const TYPES = { AOP: "AOP", BXY: "BXY", AOO: "AOO" };
  const aopPromotions = eligiblePromotions.filter((p) => p.promotion_type === TYPES.AOP);
  const bxyPromotions = eligiblePromotions.filter((p) => p.promotion_type === TYPES.BXY);
  const aooPromotions = eligiblePromotions.filter((p) => p.promotion_type === TYPES.AOO);

  const lineDiscounts = {};
  const appliedPromotions = [];

  const applyLineResult = (result) => {
    if (!result || result.totalDiscount <= 0) return;
    Object.entries(result.lineDiscounts).forEach(([index, discount]) => {
      const lineBase = getLineBaseTotal(items[index]);
      lineDiscounts[index] = Math.min(
        roundMoney((lineDiscounts[index] || 0) + discount),
        lineBase
      );
      items[index].promotionLabels.push(result.promotion.name);
    });
    appliedPromotions.push({
      id: result.promotion.id,
      name: result.promotion.name,
      promotion_type: result.promotion.promotion_type,
      discount: roundMoney(result.totalDiscount)
    });
  };

  const applyOrderResult = (result) => {
    if (!result || result.totalDiscount <= 0) return;
    const eligibleLines = items
      .map((item, index) => ({
        index,
        remaining: isItemDiscountable(item)
          ? Math.max(getLineBaseTotal(item) - toNumber(lineDiscounts[index]) - toNumber(item.wholeDiscount), 0)
          : 0
      }))
      .filter((l) => l.remaining > 0);

    const remainingSubtotal = eligibleLines.reduce((s, l) => s + l.remaining, 0);
    let distributed = 0;

    eligibleLines.forEach((line, i) => {
      if (remainingSubtotal <= 0) return;
      const item = items[line.index];
      const isLast = i === eligibleLines.length - 1;
      const share = isLast
        ? roundMoney(result.totalDiscount - distributed)
        : roundMoney(result.totalDiscount * (line.remaining / remainingSubtotal));
      const applied = Math.min(share, line.remaining);
      item.wholeDiscount = roundMoney((item.wholeDiscount || 0) + applied);
      distributed = roundMoney(distributed + applied);
      item.promotionLabels.push(result.promotion.name);
    });

    appliedPromotions.push({
      id: result.promotion.id,
      name: result.promotion.name,
      promotion_type: result.promotion.promotion_type,
      discount: roundMoney(result.totalDiscount)
    });
  };

  // ── Phase 1: exactly one non-stackable promotion may win the shared
  // exclusive slot, chosen by comparing its discount against every OTHER
  // type too — not just same-type competitors — since stacking is opt-in
  // per-promotion, not per-type.
  let bestAOP = null;
  aopPromotions.filter((p) => !isStackablePromotion(p)).forEach((p) => {
    bestAOP = chooseBetterResult(bestAOP, computeAOPResult(p, items, {}));
  });
  let bestBXY = null;
  bxyPromotions.filter((p) => !isStackablePromotion(p)).forEach((p) => {
    bestBXY = chooseBetterResult(bestBXY, buildBXYCandidate(p, items));
  });
  let bestAOO = null;
  aooPromotions.filter((p) => !isStackablePromotion(p)).forEach((p) => {
    bestAOO = chooseBetterResult(bestAOO, computeAOOResult(p, items, {}, 0));
  });

  let winner = null;
  [
    { type: TYPES.AOP, result: bestAOP },
    { type: TYPES.BXY, result: bestBXY },
    { type: TYPES.AOO, result: bestAOO }
  ].forEach(({ type, result }) => {
    if (!result || result.totalDiscount <= 0) return;
    if (!winner || result.totalDiscount > winner.result.totalDiscount) winner = { type, result };
  });

  const claimedBxyLines = new Set();
  if (winner) {
    if (winner.type === TYPES.AOO) {
      applyOrderResult(winner.result);
    } else {
      applyLineResult(winner.result);
      if (winner.type === TYPES.BXY) {
        Object.keys(winner.result.lineDiscounts).forEach((index) => claimedBxyLines.add(index));
      }
    }
  }

  // ── Phase 2: every stackable promotion (any type) applies in addition,
  // compounding on whatever's left after the exclusive winner (if any).
  aopPromotions.filter(isStackablePromotion).forEach((promotion) => {
    applyLineResult(computeAOPResult(promotion, items, lineDiscounts));
  });

  const bxyStackableCandidates = bxyPromotions
    .filter(isStackablePromotion)
    .map((promotion) => buildBXYCandidate(promotion, items))
    .filter((c) => c && c.totalDiscount > 0);
  bxyStackableCandidates.sort((a, b) => b.totalDiscount - a.totalDiscount);
  bxyStackableCandidates.forEach((candidate) => {
    const lineIndexes = Object.keys(candidate.lineDiscounts);
    const conflicts = lineIndexes.some((index) => claimedBxyLines.has(index));
    if (conflicts) return;
    lineIndexes.forEach((index) => claimedBxyLines.add(index));
    applyLineResult(candidate);
  });

  aooPromotions.filter(isStackablePromotion).forEach((promotion) => {
    const reservedTotal = items.reduce((sum, item) => sum + toNumber(item.wholeDiscount), 0);
    applyOrderResult(computeAOOResult(promotion, items, lineDiscounts, reservedTotal));
  });

  let discountTotal = 0;
  items.forEach((item, index) => {
    const itemDiscount = Math.min(toNumber(lineDiscounts[index]), getLineBaseTotal(item));
    item.discount = roundMoney(itemDiscount);
    item.wholeDiscount = roundMoney(item.wholeDiscount || 0);
    discountTotal = roundMoney(discountTotal + item.discount + item.wholeDiscount);
  });

  return { items, appliedPromotions, discountTotal };
}

// ─── PromotionService class (API + Vuex compatibility) ────────────────────────

class PromotionService {
  async getActivePromotions() {
    try {
      const url = makeUrl("/promotions");
      const response = await authApiCall(url, "get");
      return response.results || [];
    } catch (error) {
      console.error("Error fetching promotions:", error);
      return [];
    }
  }

  // Schedule/channel only — used to build the general "currently live" list
  // (promotions/activePromotions), before any employee/customer is known.
  // Audience targeting (apply_to) is resolved later, in applyPromotionsToCart.
  isPromotionActive(promotion) {
    return isPromotionInScheduleWindow(promotion);
  }
}

export default new PromotionService();
